/**
 * A ticket's realtime — Step 60.
 *
 * Two things a ticket page wants from the socket, and they belong together
 * because they share one subscription:
 *
 *  - **who else is here** ("Also viewing: Kai"), which is the collision
 *    detection this step exists for;
 *  - **it changed**, which invalidates the detail query so the change arrives
 *    without a refresh.
 *
 * `stale` is the fallback signal: when the socket is down the caller polls at
 * 30s, per the plan. A page that breaks when the socket drops has made an
 * optimisation into a dependency.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryClient';
import { isLive, onLiveChange, subscribe } from '../../lib/realtime';

export function useTicketRealtime(
  ticketId: string,
  /** The signed-in person, so a change YOU made is not announced back to you. */
  selfId: string | undefined,
): {
  alsoViewing: { userId: string; name: string }[];
  live: boolean;
  /** Who last changed this ticket from somewhere else, until dismissed. */
  changedBy: string | null;
  dismissChange: () => void;
} {
  const qc = useQueryClient();
  const [alsoViewing, setAlsoViewing] = useState<{ userId: string; name: string }[]>([]);
  const [changedBy, setChangedBy] = useState<string | null>(null);
  const [live, setLive] = useState(isLive());

  useEffect(() => onLiveChange(setLive), []);

  useEffect(() => {
    if (!ticketId) return;
    setAlsoViewing([]);
    setChangedBy(null);
    return subscribe(`ticket:${ticketId}`, {
      onChange: (signal) => {
        // Invalidate, never patch from the socket: the payload carries no
        // entity, and the refetch is what re-applies permission.
        void qc.invalidateQueries({ queryKey: qk.tickets.detail(ticketId) });
        void qc.invalidateQueries({ queryKey: qk.tickets.activity(ticketId) });
        /**
         * The approval chain too — Step 194.
         *
         * A decision by SOMEBODY ELSE is the one change to a ticket that the
         * viewer can act on immediately: it is what releases a change from
         * Awaiting approval. Kai: *"It should instantly allow me to move to
         * pending release etc if I've approved it or if someone else has."*
         * The detail query carries the gate, this one carries the tab, and
         * refetching one without the other leaves the two disagreeing on
         * screen.
         */
        void qc.invalidateQueries({ queryKey: qk.approvals.entity('ticket', ticketId) });
        void qc.invalidateQueries({ queryKey: qk.approvals.mine });
        /**
         * Announce it — but only when it was somebody else.
         *
         * Kai: *"most importantly it shows that it's updated by someone or
         * something else."* Your own save coming back as "Kai updated this
         * ticket" is noise that teaches people to ignore the banner, which is
         * exactly when the one that matters arrives.
         */
        if (signal.actorId && signal.actorId !== selfId) {
          setChangedBy(signal.actorName ?? 'Someone');
        }
      },
      // The server has already filtered the viewer out of their own list.
      onPresence: setAlsoViewing,
    });
  }, [ticketId, qc, selfId]);

  return { alsoViewing, live, changedBy, dismissChange: () => setChangedBy(null) };
}
